import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Camera, ChevronDown, ChevronUp, ExternalLink, FileArchive, Image as ImageIcon, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { SectionCard } from "./ui";
import { DesignOrderForm } from "./DesignOrderForm";
import { StageProceedingPanel } from "./StageProceedingPanel";
import { buildMdcpLightbox, MdcpPhotoLightbox } from "./MdcpPhotoLightbox";
import { createMdcpBuffer, createMdcpOrder, createMdcpProceeding, deleteMdcpBuffer, deleteMdcpPoint, deleteMdcpPointPhoto, downloadMdcpPhotosZip, getMdcpContractors, getMdcpPanel, releaseMdcpProceeding, reseedMdcpBuffer, suggestMdcpItems, updateMdcpBuffer, updateMdcpPoint } from "../api";
import type { MdcpCoverageStatus } from "../api";
import type { StageProceedingInput } from "../types";
import { apiErr } from "../utils";

/** Short verdict of the contractor's declared area vs the order area — the picker label. */
export const MDCP_COVERAGE_SHORT: Record<MdcpCoverageStatus, string> = {
  covers: "obszar pokrywa",
  missing: "brak obszaru",
  unknown_area: "obszar zamówienia nieznany",
  outside: "poza obszarem działania",
};

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const mdcpQueryKey = (connectionId: string) => ["pp", "mdcp", connectionId] as const;

export function useMdcpPanel(connectionId: string) {
  return useQuery({
    queryKey: mdcpQueryKey(connectionId),
    queryFn: () => getMdcpPanel(connectionId),
    staleTime: 15_000,
  });
}

/** Panel MDCP na mapie projektu: punkty zdjęciowe (numer + azymut + notatka),
 *  zlecenie geodecie (Requisition→Order jednym ruchem, wzór etapów projektowych)
 *  i lista zamówień MDCP. Punkty przekazane geodecie są zamrożone — zostaje
 *  odhaczanie wykonania przy odbiorze. */
export function MdcpSectionCard({ connectionId, addMode, onToggleAddMode, bufferEdit, onToggleBufferEdit, bufferDraw, onToggleBufferDraw, selectedId, onSelect, onExpandedChange }: {
  connectionId: string;
  addMode: boolean;
  onToggleAddMode: (v: boolean) => void;
  bufferEdit: boolean;
  onToggleBufferEdit: (v: boolean) => void;
  /** Rysowanie NOWEGO obrysu (zakres kolejnego zamówienia) — poligon klikany na mapie. */
  bufferDraw: boolean;
  onToggleBufferDraw: (v: boolean) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Reports the panel's expanded state so the map can show the buffer only while expanded. */
  onExpandedChange?: (expanded: boolean) => void;
}) {
  const qc = useQueryClient();
  const { data } = useMdcpPanel(connectionId);
  const inval = () => qc.invalidateQueries({ queryKey: mdcpQueryKey(connectionId) });
  const patchM = useMutation({
    mutationFn: (v: { id: string; patch: Parameters<typeof updateMdcpPoint>[1] }) =>
      updateMdcpPoint(v.id, v.patch),
    onSuccess: inval,
  });
  // The server REFUSES to delete a point held by a surveyor's order or by a published
  // postępowanie ofertowe. Until that refusal was rendered the trash icon read as broken:
  // the request went out, 400 came back, and nothing changed on screen.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const delM = useMutation({
    mutationFn: (id: string) => deleteMdcpPoint(id),
    onSuccess: () => { setDeleteError(null); inval(); },
    onError: (e) => setDeleteError(apiErr(e, "Nie udało się usunąć punktu.")),
  });
  // Zamówienie zdjęte → punkt wraca do edycji, ale zdjęcia od geodety zostają; kasujemy je ręcznie.
  const photoDelM = useMutation({
    mutationFn: (photoId: string) => deleteMdcpPointPhoto(photoId),
    onSuccess: () => { setDeleteError(null); inval(); },
    onError: (e) => setDeleteError(apiErr(e, "Nie udało się usunąć zdjęcia.")),
  });
  const orderM = useMutation({
    mutationFn: (fd: FormData) => createMdcpOrder(connectionId, fd),
    onSuccess: () => { setShowForm(false); inval(); },
  });
  // Druga droga wyłonienia geodety: postępowanie ofertowe na ten sam zakres.
  const startRfqM = useMutation({
    mutationFn: (payload: StageProceedingInput) => createMdcpProceeding(connectionId, payload),
    onSuccess: inval,
  });
  const releaseRfqM = useMutation({
    mutationFn: () => releaseMdcpProceeding(connectionId),
    onSuccess: inval,
  });
  const [showForm, setShowForm] = useState(false);
  // Only surveyors whose framework agreement has MDCP-ish positions, with distance to
  // the project (geocoding + routing on the backend — fetched lazily when the form opens).
  const contractorsQ = useQuery({
    queryKey: ["pp", "mdcp-contractors", connectionId],
    queryFn: () => getMdcpContractors(connectionId),
    // Przy zleceniu zwycięzcy picker firm nie istnieje (wykonawcę ustala postępowanie),
    // więc nie odpalamy kosztownego liczenia odległości do projektu.
    enabled: showForm && !data?.proceeding?.awaiting_order,
    staleTime: 5 * 60_000,
  });
  // Kolejność z backendu: obszar działania pokrywa → brak obszaru → obszar poza zamówieniem,
  // w każdej grupie po odległości. Etykieta i podpowiedź mówią wprost, DLACZEGO firma
  // jest tam, gdzie jest — użytkownik nie ma zgadywać, co promowało firmę na górę.
  const contractorOptions = (contractorsQ.data?.contractors ?? []).map((c) => ({
    value: c.id,
    label:
      `${c.name}${c.distance_km != null ? ` · ~${Math.round(c.distance_km)} km` : ""}` +
      ` · ${MDCP_COVERAGE_SHORT[c.coverage_status]}`,
    hint:
      (c.distance_km != null
        ? `Siedziba/baza ~${Math.round(c.distance_km)} km od projektu${c.base_label ? ` (${c.base_label})` : ""}.`
        : "Odległość siedziby od projektu nieznana (nie udało się zlokalizować adresu).") +
      ` ${capitalize(c.coverage_label)}.` +
      (c.matched_items.length ? ` Pozycje MDCP: ${c.matched_items.join("; ")}.` : "") +
      (c.eligible_via?.includes("work_type") ? " Typ prac „Geodeta” w kartotece." : "") +
      (c.eligible_via?.includes("work_type") && !c.matched_items.length
        ? " Uwaga: brak pozycji MDCP w cenniku umowy ramowej."
        : ""),
  }));
  const orderAreaLabel = contractorsQ.data?.area_label ?? "";

  const zipM = useMutation({ mutationFn: () => downloadMdcpPhotosZip(connectionId) });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Bufory (obrysy) MDCP: projekt ma ich wiele — `buffer` to wybrany z listy rozwijanej,
  // czyli zakres, który zamrozi kolejne zlecenie. Szerokość konfigurowalna, „Przywróć auto"
  // cofa ręczne edycje WYBRANEGO obrysu; poprzednie obrysy zostają nietknięte.
  const buffers = data?.buffers ?? [];
  const buffer = data?.buffer ?? null;
  const [widthDraft, setWidthDraft] = useState<string>("");
  useEffect(() => {
    if (buffer && document.activeElement?.getAttribute("data-mdcp-width") == null) {
      setWidthDraft(String(buffer.buffer_width_m));
    }
  }, [buffer]);
  // Odmowę serwera (np. kasowanie obrysu, na którym stoi zamówienie) trzeba pokazać —
  // inaczej przycisk wygląda na zepsuty: leci request, wraca 400, na ekranie nic.
  const [bufferError, setBufferError] = useState<string | null>(null);
  const bufferMutation = <T,>(fn: (v: T) => Promise<unknown>, fallback: string) =>
    ({
      mutationFn: fn,
      onSuccess: () => { setBufferError(null); inval(); },
      onError: (e: unknown) => setBufferError(apiErr(e, fallback)),
    });
  const widthM = useMutation(bufferMutation(
    (w: number) => updateMdcpBuffer(connectionId, buffer!.id, { buffer_width_m: w }),
    "Nie udało się zmienić szerokości obrysu.",
  ));
  const reseedM = useMutation(bufferMutation<void>(
    () => reseedMdcpBuffer(connectionId, buffer!.id),
    "Nie udało się przywrócić automatycznego obrysu.",
  ));
  const selectM = useMutation(bufferMutation(
    (id: string) => updateMdcpBuffer(connectionId, id, { is_active: true }),
    "Nie udało się przełączyć obrysu.",
  ));
  const renameM = useMutation(bufferMutation(
    (v: { id: string; name: string }) => updateMdcpBuffer(connectionId, v.id, { name: v.name }),
    "Nie udało się zmienić nazwy obrysu.",
  ));
  const addM = useMutation(bufferMutation<void>(
    () => createMdcpBuffer(connectionId, {}),
    "Nie udało się dodać obrysu.",
  ));
  const deleteBufferM = useMutation(bufferMutation(
    (id: string) => deleteMdcpBuffer(connectionId, id),
    "Nie udało się usunąć obrysu.",
  ));
  const bufferBusy =
    widthM.isPending || reseedM.isPending || selectM.isPending ||
    renameM.isPending || addM.isPending || deleteBufferM.isPending;
  const applyWidth = () => {
    const w = Number(widthDraft.replace(",", "."));
    if (!Number.isFinite(w) || w < 1 || w > 500) return;
    if (!buffer || w === buffer.buffer_width_m) return;
    widthM.mutate(w);
  };
  /** Etykieta pozycji listy: nazwa + powierzchnia + zamówienia, które na obrysie stoją. */
  const bufferLabel = (b: (typeof buffers)[number]) =>
    [
      b.display_name,
      b.area_m2 != null ? `${(b.area_m2 / 10000).toFixed(2)} ha` : "brak obrysu",
      b.order_numbers.length ? `zlecone: ${b.order_numbers.join(", ")}` : null,
    ]
      .filter(Boolean)
      .join(" · ");

  const points = data?.points ?? [];
  const orders = data?.orders ?? [];
  // Kontrola ilości: ile „szt. 100 mb" wychodzi z korytarza trasy kontra ile realnie
  // poszło w zamówieniu. Ostrzegamy tylko przy zamówieniu ponad tę liczbę — mniej niż
  // wyliczone bywa świadomym zakresem (np. część trasy mierzona wcześniej).
  const routeLength = data?.route_length ?? {};
  const expectedUnits = routeLength.expected_units ?? null;
  const overOrdered = (() => {
    if (expectedUnits == null) return null;
    const worst = orders
      .filter((o) => o.ordered_length_units != null && o.ordered_length_units > expectedUnits)
      .sort((a, b) => (b.ordered_length_units ?? 0) - (a.ordered_length_units ?? 0))[0];
    return worst ? { ordered: worst.ordered_length_units as number } : null;
  })();
  // To samo dla geodetów rozliczających za HEKTAR: pas 35 m w każdą stronę od trasy
  // (70 m), nakładające się odcinki liczone raz; porównujemy z rozpoczętymi hektarami.
  const corridorAreaHa = routeLength.corridor_area_ha ?? null;
  const expectedAreaUnits = routeLength.expected_area_units ?? null;
  const halfWidthM = routeLength.area_half_width_m ?? 35;
  const overOrderedArea = (() => {
    if (expectedAreaUnits == null) return null;
    const worst = orders
      .filter((o) => o.ordered_area_units != null && o.ordered_area_units > expectedAreaUnits)
      .sort((a, b) => (b.ordered_area_units ?? 0) - (a.ordered_area_units ?? 0))[0];
    return worst ? { ordered: worst.ordered_area_units as number } : null;
  })();
  // „Wolny" punkt to taki, którego nie trzyma ani zlecenie, ani opublikowane ogłoszenie.
  const unclaimed = points.filter((p) => !p.order_link && !p.rfq_link);
  const proceeding = data?.proceeding ?? null;
  const rfqWinner =
    proceeding?.awaiting_order && proceeding.winner
      ? {
          proceedingId: proceeding.id,
          number: proceeding.number,
          title: proceeding.title,
          contractorName: proceeding.winner.contractor_name,
          amountNet: proceeding.winner.amount_net,
        }
      : undefined;
  const doneCount = points.filter((p) => p.is_done).length;
  const photoCount = points.reduce((n, p) => n + p.photos_count, 0);

  // Flat photo list (all points) — powers the lightbox; index of each point's first photo
  // lets a row's badge open the viewer right at that point.
  const allPhotos = buildMdcpLightbox(points);
  const firstPhotoIndex = new Map<string, number>();
  allPhotos.forEach((ph, i) => {
    if (!firstPhotoIndex.has(ph.id)) firstPhotoIndex.set(ph.id, i);
  });
  // Collapsed by default: the map draws the photo points and the buffer only while this panel
  // is expanded, and on the map they clutter the route view — so MDCP is opt-in per visit.
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);
  const expanded = expandedOverride ?? false;
  useEffect(() => {
    // Selecting a point on the map should reveal its row even if the panel is collapsed.
    if (selectedId) setExpandedOverride(true);
  }, [selectedId]);
  useEffect(() => {
    // The map draws the MDCP buffer only while this panel is expanded.
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-1.5">
          <Camera size={14} /> MDCP — punkty zdjęciowe
        </span>
      }
      action={
        <span className="inline-flex items-center gap-2">
          {points.length > 0 && (
            <span className="text-xs text-[var(--color-text-muted)]">{doneCount}/{points.length} wykonane</span>
          )}
          {photoCount > 0 && (
            <button
              type="button"
              disabled={zipM.isPending}
              onClick={() => zipM.mutate()}
              title="Pobierz wszystkie zdjęcia jako ZIP (PUNKT_<nr>__ZDJ_<i>)"
              className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-60"
            >
              {zipM.isPending ? <Spinner /> : <FileArchive size={13} />} ZIP
            </button>
          )}
          <button
            type="button"
            className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            title={expanded ? "Zwiń panel" : "Rozwiń panel"}
            onClick={() => setExpandedOverride(!expanded)}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </span>
      }
      bodyClassName={expanded ? undefined : "hidden"}
    >
      <div className="space-y-3">
        <Button
          variant={addMode ? "primary" : "secondary"}
          className="w-full"
          onClick={() => onToggleAddMode(!addMode)}
        >
          {addMode ? "✓ Zakończ dodawanie punktów" : "+ Dodaj punkty na mapie"}
        </Button>
        {addMode && (
          <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
            Klik na pustej mapie = nowy punkt (numer nada się sam). Przeciągnij punkt, żeby
            go przesunąć; klik w punkt obraca kierunek zdjęcia o 45° zgodnie z ruchem
            wskazówek zegara (azymut, 0° = północ). Dokładny azymut ustawisz też poniżej.
          </p>
        )}

        {/* Bufor (obrys) MDCP — podgląd + konfiguracja zakresu dla geodety. */}
        <div className="rounded border border-[var(--color-border-muted)] p-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-[var(--color-text-secondary)]">
              Bufor MDCP (obrys dla geodety)
            </span>
            {buffer?.area_m2 != null && (
              <span className="text-[11px] text-[var(--color-text-muted)]">
                {(buffer.area_m2 / 10000).toFixed(2)} ha
              </span>
            )}
          </div>
          {/* Lista obrysów projektu: kolejne zamówienie bierze zwykle inny zakres, więc
              obrysów jest wiele, a ten wybrany idzie na mapę i do najbliższego zlecenia.
              Przy pozycjach stoją numery zamówień, które na danym obrysie zlecono. */}
          {buffers.length > 0 && (
            <div className="flex items-center gap-1">
              <select
                aria-label="Wybrany obrys MDCP"
                className="input-base flex-1 min-w-0 py-0.5 text-xs"
                value={buffer?.id ?? ""}
                disabled={bufferBusy}
                onChange={(e) => {
                  onToggleBufferEdit(false); // uchwyty edycji zostałyby na starym obrysie
                  selectM.mutate(e.target.value);
                }}
              >
                {buffers.map((b) => (
                  <option key={b.id} value={b.id}>
                    {bufferLabel(b)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                title="Zmień nazwę obrysu"
                disabled={bufferBusy || !buffer}
                onClick={() => {
                  if (!buffer) return;
                  const name = window.prompt("Nazwa obrysu:", buffer.name || buffer.display_name);
                  if (name != null) renameM.mutate({ id: buffer.id, name });
                }}
                className="shrink-0 rounded border border-[var(--color-border-muted)] p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] disabled:opacity-60"
              >
                <Pencil size={12} />
              </button>
              <button
                type="button"
                title="Usuń ten obrys"
                disabled={bufferBusy || !buffer || buffers.length < 2}
                onClick={() => {
                  if (!buffer) return;
                  if (window.confirm(`Usunąć obrys „${buffer.display_name}"?`)) {
                    onToggleBufferEdit(false);
                    deleteBufferM.mutate(buffer.id);
                  }
                }}
                className="shrink-0 rounded border border-[var(--color-border-muted)] p-1 text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-60"
              >
                <Trash2 size={12} />
              </button>
              {bufferBusy && <Spinner />}
            </div>
          )}
          {(buffer?.order_numbers.length ?? 0) > 0 && (
            <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
              Ten obrys poszedł w zamówienie: <b>{buffer?.order_numbers.join(", ")}</b>. Zmiany
              tutaj nie ruszają zamrożonego zakresu u geodety — pod nowy zakres załóż kolejny obrys.
            </p>
          )}
          {/* Ilości rozliczeniowe z trasy — obie konwencje cenników geodetów: za 100 mb
              (korytarz) i za hektar (pas 35 m w każdą stronę, nakładki liczone raz).
              Te liczby dostaje też podpowiedź AI w formularzu zamówienia. */}
          {(expectedUnits != null || corridorAreaHa != null) && (
            <p
              className="text-[11px] text-[var(--color-text-muted)] leading-snug"
              title="Ilości do zamówienia MDCP wyliczone z obowiązującej trasy. Korytarz: metry terenu, nitki biegnące razem liczone raz. Pas: bufor 35 m w każdą stronę od osi trasy (70 m szerokości), nakładające się bufory liczone raz."
            >
              Z trasy:{" "}
              {expectedUnits != null && (
                <>
                  korytarz {Math.round(routeLength.corridor_length_m ?? 0)} m →{" "}
                  <b>{expectedUnits} szt. 100 mb</b>
                </>
              )}
              {expectedUnits != null && corridorAreaHa != null && " · "}
              {corridorAreaHa != null && (
                <>
                  pas {2 * halfWidthM} m → <b>{corridorAreaHa.toFixed(2)} ha</b>
                  {expectedAreaUnits != null && ` (${expectedAreaUnits} rozp. ha)`}
                </>
              )}
            </p>
          )}
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-[var(--color-text-muted)]">Szerokość [m]</label>
            <input
              type="number"
              min={1}
              max={500}
              step={1}
              data-mdcp-width
              value={widthDraft}
              disabled={bufferBusy || !buffer}
              onChange={(e) => setWidthDraft(e.target.value)}
              onBlur={applyWidth}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
              }}
              className="input-base w-20 py-0.5 text-xs"
            />
          </div>
          <Button
            variant={bufferEdit ? "primary" : "secondary"}
            className="w-full"
            disabled={!buffer?.geometry}
            onClick={() => {
              if (!bufferEdit) onToggleAddMode(false); // bufor i punkty dzielą klik na mapie
              onToggleBufferEdit(!bufferEdit);
            }}
          >
            {bufferEdit ? "✓ Zakończ edycję buforu" : "Edytuj bufor"}
          </Button>
          {!buffer?.geometry && (
            <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
              Brak obrysu — narysuj najpierw trasę projektu; bufor policzy się automatycznie.
            </p>
          )}
          {bufferEdit && (
            <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
              Przeciągaj wierzchołki obrysu do wewnątrz, żeby wyłączyć teren, za który nie
              płacimy (nasyp kolejowy, rzeka, las). Zmiany zapisują się od razu.
            </p>
          )}
          {/* Kolejny obrys DOKŁADA się do listy (dawniej: nadpisywał jedyny bufor projektu,
              więc zakres wcześniejszego zamówienia przestawał istnieć). */}
          <div className="flex gap-1">
            <Button
              variant={bufferDraw ? "primary" : "secondary"}
              className="flex-1"
              onClick={() => {
                if (!bufferDraw) { onToggleAddMode(false); onToggleBufferEdit(false); } // wspólny klik na mapie
                onToggleBufferDraw(!bufferDraw);
              }}
            >
              {bufferDraw ? "✕ Anuluj rysowanie obrysu" : "+ Nowy obrys (narysuj)"}
            </Button>
            <Button
              variant="secondary"
              disabled={bufferBusy}
              title="Nowy obrys zasiany automatycznie wokół bieżącej trasy"
              onClick={() => { onToggleBufferEdit(false); onToggleBufferDraw(false); addM.mutate(); }}
            >
              + z trasy
            </Button>
          </div>
          {bufferDraw && (
            <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
              Klikaj na mapie kolejne wierzchołki nowego obrysu; zamknij poligon klikając
              pierwszy punkt. Nowy obrys DOŁĄCZY do listy i stanie się wybrany — poprzednie
              zostają, razem z zakresami zleconych już zamówień.
            </p>
          )}
          {buffer?.is_customized && (
            <button
              type="button"
              disabled={reseedM.isPending}
              onClick={() => {
                if (window.confirm("Przywrócić automatyczny obrys buforu? Ręczne edycje zostaną utracone.")) {
                  onToggleBufferEdit(false);
                  reseedM.mutate();
                }
              }}
              className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] underline disabled:opacity-60"
            >
              Przywróć auto (cofnij ręczne edycje)
            </button>
          )}
          {bufferError && (
            <p className="text-[11px] font-mono text-[var(--color-danger)]">{bufferError}</p>
          )}
        </div>

        {deleteError && (
          <p className="text-[11px] font-mono text-[var(--color-danger)]">{deleteError}</p>
        )}

        {points.length > 0 && (
          <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
            {points.map((p) =>
              p.order_link ? (
                // Point already handed off to a surveyor — frozen, so a dense one-liner:
                // number, azimuth, note as plain text, photos and the done checkbox.
                <li
                  key={p.id}
                  className={`flex items-center gap-2 rounded border px-2 py-1 text-xs cursor-pointer transition-colors ${
                    selectedId === p.id
                      ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                      : "border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
                  }`}
                  onClick={() => onSelect(selectedId === p.id ? null : p.id)}
                >
                  <span className="font-mono font-semibold shrink-0">#{p.number}</span>
                  <span className="font-mono text-[var(--color-text-muted)] shrink-0">{p.azimuth_deg}°</span>
                  {p.note && (
                    <span className="truncate text-[var(--color-text-secondary)]" title={p.note}>
                      {p.note}
                    </span>
                  )}
                  {p.photos_count > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const first = p.photos[0];
                        if (first) setLightboxIndex(firstPhotoIndex.get(first.id) ?? 0);
                      }}
                      title={`${p.photos_count} zdj. od geodety`}
                      className="inline-flex items-center gap-0.5 shrink-0 text-green-600 hover:underline"
                    >
                      <ImageIcon size={12} />
                      {p.photos_count}
                    </button>
                  )}
                  <label
                    className="ml-auto flex items-center gap-1 shrink-0 text-[11px] text-[var(--color-text-muted)]"
                    title="Wykonane (odhaczamy przy odbiorze zdjęć od geodety)"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={p.is_done}
                      onChange={(e) => patchM.mutate({ id: p.id, patch: { is_done: e.target.checked } })}
                    />
                    wykonane
                  </label>
                </li>
              ) : (
                <li
                  key={p.id}
                  className={`rounded border px-2 py-1.5 text-xs cursor-pointer transition-colors ${
                    selectedId === p.id
                      ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                      : "border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)]"
                  }`}
                  onClick={() => onSelect(selectedId === p.id ? null : p.id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold shrink-0">#{p.number}</span>
                    <label
                      className="flex items-center gap-1 text-[var(--color-text-muted)]"
                      title="Azymut [°] — kierunek zdjęcia (0 = północ, zapis przy wyjściu z pola)"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="number"
                        min={0}
                        max={359}
                        // Uncontrolled (defaultValue + commit-on-blur), but keyed on the stored
                        // azimuth so the map's click-to-rotate gesture — which mutates the value
                        // server-side — remounts the field with the fresh number instead of the
                        // stale mount-time default.
                        key={p.azimuth_deg}
                        className="input-base text-xs w-16 py-0.5 text-right"
                        defaultValue={p.azimuth_deg}
                        onBlur={(e) => {
                          const v = Math.max(0, Math.min(359, Number(e.target.value) || 0));
                          if (v !== p.azimuth_deg) patchM.mutate({ id: p.id, patch: { azimuth_deg: v } });
                        }}
                      />
                      °
                    </label>
                    <span className="ml-auto" />
                    <button
                      type="button"
                      className="p-0.5 text-[var(--color-text-muted)] hover:text-red-600 shrink-0"
                      title="Usuń punkt"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          p.photos_count > 0 &&
                          !window.confirm(
                            `Punkt #${p.number} ma ${p.photos_count} zdj. — usunięcie punktu skasuje też jego zdjęcia. Usunąć?`,
                          )
                        )
                          return;
                        delM.mutate(p.id);
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <input
                    className="input-base text-xs w-full py-0.5 mt-1.5"
                    placeholder="Uwaga dla geodety (opcjonalnie)…"
                    defaultValue={p.note}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      if (e.target.value !== p.note) patchM.mutate({ id: p.id, patch: { note: e.target.value } });
                    }}
                  />
                  {/* Punkt zwolniony po zdjęciu zamówienia, a wciąż ma zdjęcia od geodety —
                      pokaż je z ręcznym kasowaniem (nie znikają razem ze zleceniem). */}
                  {p.photos_count > 0 && (
                    <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] mb-1">
                        <ImageIcon size={11} className="text-green-600" />
                        Zdjęcia od geodety ({p.photos_count}) — zamówienie zdjęte; usuń ręcznie.
                      </div>
                      <div className="grid grid-cols-4 gap-1">
                        {p.photos.map((ph) => (
                          <div key={ph.id} className="relative group/photo">
                            <button
                              type="button"
                              title={ph.original_name}
                              onClick={() => setLightboxIndex(firstPhotoIndex.get(ph.id) ?? 0)}
                              className="block w-full cursor-zoom-in"
                            >
                              <img
                                src={ph.file?.url ?? undefined}
                                alt={ph.original_name}
                                className="h-12 w-full object-cover border border-[var(--color-border)]"
                              />
                            </button>
                            <button
                              type="button"
                              title="Usuń zdjęcie"
                              disabled={photoDelM.isPending}
                              onClick={() => photoDelM.mutate(ph.id)}
                              className="absolute top-0.5 right-0.5 bg-white/90 p-0.5 text-[var(--color-text-muted)] hover:text-red-600 opacity-0 group-hover/photo:opacity-100 transition-opacity"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ),
            )}
          </ul>
        )}

        {orders.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] mb-1">
              Zamówienia MDCP
            </div>
            <ul className="space-y-1">
              {orders.map((o) => {
                const over =
                  expectedUnits != null &&
                  o.ordered_length_units != null &&
                  o.ordered_length_units > expectedUnits;
                const overArea =
                  expectedAreaUnits != null &&
                  o.ordered_area_units != null &&
                  o.ordered_area_units > expectedAreaUnits;
                return (
                  <li key={o.id} className="text-xs flex items-center gap-1.5 flex-wrap">
                    <Link to={`/orders/${o.order}`} className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline">
                      {o.order_number} <ExternalLink size={11} />
                    </Link>
                    <span className="text-[var(--color-text-muted)]">· {o.order_status_display}</span>
                    {o.contractor_name && <span className="text-[var(--color-text-muted)]">· {o.contractor_name}</span>}
                    <span className="text-[var(--color-text-muted)]">· {o.points_count} pkt</span>
                    {o.ordered_length_units != null && (
                      <span className={over ? "text-[var(--color-primary)] font-medium" : "text-[var(--color-text-muted)]"}>
                        · {o.ordered_length_units} szt. 100 mb
                      </span>
                    )}
                    {o.ordered_area_units != null && (
                      <span className={overArea ? "text-[var(--color-primary)] font-medium" : "text-[var(--color-text-muted)]"}>
                        · {o.ordered_area_units} ha
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            {/* Ostrzeżenie o zawyżonej ilości: zamówienie policzone z sumy narysowanych
                linii, choć geodeta mierzy PAS terenu — nitki biegnące razem (np. nowy
                kabel wzdłuż likwidowanej linii napowietrznej) to jeden korytarz. */}
            {overOrdered && (
              <div className="mt-2 border-l-2 border-[var(--color-primary)] bg-[var(--color-bg-elevated)] px-2.5 py-2 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-medium text-[var(--color-primary)]">
                  <AlertTriangle size={13} className="shrink-0" />
                  Zamówiona ilość wyższa niż wynika z trasy
                </div>
                <div className="text-[var(--color-text-secondary)]">
                  W zamówieniu: <b>{overOrdered.ordered} szt.</b> ·
                  {" "}z długości trasy {Math.round(routeLength.length_m ?? 0)} m wychodzi{" "}
                  {routeLength.units_from_length} szt.
                </div>
                <div className="text-[var(--color-text-secondary)]">
                  Korytarz (metry terenu, nitki biegnące razem liczone raz):{" "}
                  <b>{Math.round(routeLength.corridor_length_m ?? 0)} m → {expectedUnits} szt.</b>
                </div>
                <div className="text-[var(--color-text-muted)]">
                  Różnica: {overOrdered.ordered - (expectedUnits ?? 0)} szt. Sprawdź zakres u geodety
                  przed rozliczeniem.
                </div>
              </div>
            )}
            {/* Ta sama kontrola dla rozliczenia za hektar: więcej hektarów niż rozpoczętych
                w pasie 70 m wzdłuż trasy (nakładające się odcinki liczone raz). */}
            {overOrderedArea && (
              <div className="mt-2 border-l-2 border-[var(--color-primary)] bg-[var(--color-bg-elevated)] px-2.5 py-2 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-medium text-[var(--color-primary)]">
                  <AlertTriangle size={13} className="shrink-0" />
                  Zamówione hektary wyższe niż wynika z trasy
                </div>
                <div className="text-[var(--color-text-secondary)]">
                  W zamówieniu: <b>{overOrderedArea.ordered} ha</b> · pas {2 * halfWidthM} m wzdłuż
                  trasy ({halfWidthM} m w każdą stronę, nakładki liczone raz):{" "}
                  <b>{(corridorAreaHa ?? 0).toFixed(2)} ha → {expectedAreaUnits} rozpoczętych ha</b>
                </div>
                <div className="text-[var(--color-text-muted)]">
                  Różnica: {Math.round((overOrderedArea.ordered - (expectedAreaUnits ?? 0)) * 100) / 100} ha.
                  Sprawdź zakres u geodety przed rozliczeniem.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pierwsze zlecenie wymaga wolnych punktów foto; potem przycisk zostaje jako
            „Zamów kolejne MDCP" (rozszerzenie zakresu — zwykle po narysowaniu nowego
            obrysu, punkty opcjonalne). Trwające postępowanie blokuje zlecenie wprost —
            wtedy zostaje „Zleć zwycięzcy". */}
        {!showForm ? (
          <div className="space-y-2">
            {!proceeding?.blocks_direct_order && (
              <Button
                variant="secondary"
                className="w-full"
                disabled={unclaimed.length === 0 && orders.length === 0}
                title={
                  unclaimed.length === 0 && orders.length === 0
                    ? "Najpierw dodaj punkty na mapie"
                    : undefined
                }
                onClick={() => setShowForm(true)}
              >
                {unclaimed.length > 0
                  ? `Zleć MDCP geodecie (${unclaimed.length} pkt)`
                  : "Zamów kolejne MDCP"}
              </Button>
            )}
            {orders.length > 0 && unclaimed.length === 0 && !proceeding?.blocks_direct_order && (
              <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
                Kolejne zamówienie obejmie WYBRANY obrys buforu (bez nowych punktów foto) —
                dodaj wcześniej nowy obrys dla rozszerzanego zakresu albo wybierz go z listy.
              </p>
            )}
            <StageProceedingPanel
              proceeding={proceeding}
              canManage
              stage="mdcp"
              connectionId={connectionId}
              starting={startRfqM.isPending}
              releasing={releaseRfqM.isPending}
              onStart={(payload) => startRfqM.mutate(payload)}
              onRelease={() => releaseRfqM.mutate()}
              onOrderWinner={() => setShowForm(true)}
              orderFormOpen={showForm}
            />
            {(startRfqM.isError || releaseRfqM.isError) && (
              <p className="text-[11px] font-mono text-[var(--color-primary)]">
                {apiErr(startRfqM.error ?? releaseRfqM.error)}
              </p>
            )}
          </div>
        ) : (
          <DesignOrderForm
            winner={rfqWinner}
            pending={orderM.isPending}
            onSubmit={(fd) => orderM.mutate(fd)}
            onCancel={() => setShowForm(false)}
            aiSuggest={rfqWinner ? undefined : (contractorId) => suggestMdcpItems(connectionId, contractorId)}
            checkpointPath="mdcp"
            contractorsOverride={{
              options: contractorOptions,
              loading: contractorsQ.isLoading,
              note: orderAreaLabel
                ? `Obszar zamówienia (z działek pod trasą): ${orderAreaLabel}. Kolejność firm: obszar działania pokrywa → brak wpisanego obszaru → obszar poza zamówieniem; w każdej grupie po odległości.`
                : "Obszar zamówienia nieznany (działki bez TERYT) — firmy w kolejności odległości od projektu.",
              emptyText:
                "Żaden aktywny podwykonawca nie ma pozycji MDCP („MDCP”, „mapa do celów projektowych”) w umowie ramowej.",
            }}
          />
        )}
        {orderM.isError && (
          <p className="text-[11px] font-mono text-[var(--color-primary)]">{apiErr(orderM.error)}</p>
        )}
      </div>

      {lightboxIndex !== null && allPhotos.length > 0 && (
        <MdcpPhotoLightbox
          photos={allPhotos}
          index={Math.min(lightboxIndex, allPhotos.length - 1)}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onDownloadZip={() => zipM.mutate()}
          zipPending={zipM.isPending}
        />
      )}
    </SectionCard>
  );
}
